import classnames from 'classnames'
import * as React from 'react'
import * as Reakit from 'reakit/Composite'

import { ListContext } from './hooks'
import { ListItem } from './list-item'

interface ListProps extends Omit<Reakit.CompositeHTMLProps, 'onChange'> {
  /**
   * The returned object from `useListState`
   */
  state: Reakit.CompositeStateReturn

  /**
   * The selected option
   */
  selectedIndex?: number

  /**
   * Callback when selected option index changes
   */
  onChange?: (selectedIndex: number, closeParent?: boolean) => void

  /**
   * Whether or not default styles should be included for the list
   * @default true
   */
  includeBaseStyles?: boolean
}

export function List({ state, selectedIndex, includeBaseStyles = true, onChange, children, ...props }: ListProps) {
  const prevSelected = React.useRef<number | null>(null)

  // Select option when `selectedIndex` changes
  React.useEffect(() => {
    if (
      selectedIndex !== undefined &&
      selectedIndex >= 0 &&
      prevSelected.current !== selectedIndex &&
      state.items.length > selectedIndex
    ) {
      state.move(state.items[selectedIndex].id)
      prevSelected.current = selectedIndex
    }
  }, [selectedIndex, state, state.items.length])

  const onSelectItem = React.useCallback(
    (id: string, closeParent: boolean) => {
      onChange?.(
        state.items.findIndex((item) => item.id === id),
        closeParent
      )
    },
    [onChange, state.items]
  )

  return (
    <ListContext.Provider value={state}>
      <Reakit.Composite
        {...state}
        {...props}
        role="list"
        className={classnames('outline-none', props.className, {
          'flex flex-col items-stretch space-y-px': includeBaseStyles,
        })}
      >
        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) return child

          if (child.type === ListItem) {
            return React.cloneElement(child, { ...child.props, onSelectItem: onSelectItem })
          }

          return child
        })}
      </Reakit.Composite>
    </ListContext.Provider>
  )
}
